import { useState, useEffect, useRef } from "react";
import { getApiUrl } from "../../../../api";
import { useAuthStore } from "../../../../store/authStore";
import {
  EmployeeCallIcon,
  HighContrastIcon,
  VolumeIcon,
  LanguageIcon,
  ZoomIcon,
  CustomerServiceIcon,
  ScreenTransitionIcon,
  ChevronDown,
  ChevronUp,
  SettingsIcon,
} from "./AccessibilityIcons";

// =============================================
// API
// =============================================
async function fetchSettingsByKiosk(kiosk_code: string) {
  const res = await fetch(getApiUrl(`/api/solutions/by-kiosk/${kiosk_code}/settings`), {
    headers: { Authorization: `Bearer ${localStorage.getItem("cms_access_token")}` },
  });
  if (!res.ok) throw new Error("세팅 조회 실패");
  return res.json();
}

async function patchSetting(solution_code: string, endpoint: string, body: any) {
  const res = await fetch(getApiUrl(`/api/solutions/${solution_code}/${endpoint}`), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("cms_access_token")}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("저장 실패");
  return res.json();
}

interface ContentProps {
  data: any;
  solutionCode: string;
  onSaved?: () => void;
  saveRef: React.MutableRefObject<(() => Promise<void>) | null>;
  setSaving: (v: boolean) => void;
  showModal: (title: string, type?: string) => void;
}

// =============================================
// 직원호출
// =============================================
function EmployeeCallContent({ data, solutionCode, onSaved, saveRef, setSaving, showModal }: ContentProps) {
  const { role } = useAuthStore();
  const [enabled, setEnabled] = useState(data?.staff_call_enabled === 1);
  const [phone, setPhone] = useState(data?.staff_call_number || "");
  const [message, setMessage] = useState(data?.staff_call_message || "");

  const ref2 = useRef<HTMLInputElement>(null);
  const ref3 = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEnabled(data?.staff_call_enabled === 1);
    setPhone(data?.staff_call_number || "");
    setMessage(data?.staff_call_message || "");
  }, [data]);

  const parts = phone.split("-");
  const p1 = parts[0] || "";
  const p2 = parts[1] || "";
  const p3 = parts[2] || "";

  const handlePhoneChange = (idx: number, value: string, maxLen: number, nextRef: React.RefObject<HTMLInputElement | null> | null) => {
    const onlyNum = value.replace(/\D/g, "").slice(0, maxLen);
    const newParts = [p1, p2, p3];
    newParts[idx] = onlyNum;
    setPhone(newParts.join("-"));
    if (onlyNum.length === maxLen && nextRef?.current) nextRef.current.focus();
  };

  saveRef.current = async () => {
    if (enabled && !phone.trim()) {
      showModal("호출 담당자 전화번호를 입력해주세요.");
      return;
    }
    setSaving(true);
    try {
      await patchSetting(solutionCode, "staff-call", {
        staff_call_enabled: enabled ? 1 : 0,
        staff_call_number: phone,
        staff_call_message: message,
      });
      showModal("직원호출 설정이 저장되었습니다.");
      onSaved?.();
    } catch (e: any) {
      showModal(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
      <div className="flex flex-col gap-4 flex-1">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
          <p className="text-[13px] font-semibold text-[#2a1f1a] whitespace-nowrap">카카오톡 호출 알림 사용여부</p>
          <button
            onClick={() => role > 1 && setEnabled(!enabled)}
            disabled={role > 1}
            className={`w-[88px] h-[28px] rounded-full flex items-center px-1 transition-colors flex-shrink-0 ${enabled ? "bg-[#887267]" : "bg-[#d0c4be]"} ${role === 2 ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <span className={`w-[50px] h-[24px] bg-white rounded-full flex items-center justify-center text-xs transition-transform ${enabled ? "translate-x-[34px]" : ""}`}>
              {enabled ? "사용" : "미사용"}
            </span>
          </button>
        </div>
        {enabled && (
          <div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 mb-2">
              <p className="text-[13px] font-semibold text-[#2a1f1a]">호출 담당자 전화번호</p>
              <p className="text-[11px] text-gray-400">사용자가 직원호출 버튼을 누르면 입력하신 번호로 알림이 전송됩니다.</p>
            </div>
            <div className="flex items-center gap-1">
              <input value={p1} onChange={e => handlePhoneChange(0, e.target.value, 3, ref2)} maxLength={3} placeholder="010"
                className="w-16 h-[34px] border border-[#e8e0dc] rounded-lg px-2 text-[13px] text-center outline-none focus:border-[#887267]" />
              <span className="text-gray-400 font-semibold">-</span>
              <input ref={ref2} value={p2} onChange={e => handlePhoneChange(1, e.target.value, 4, ref3)} maxLength={4} placeholder="0000"
                className="w-20 h-[34px] border border-[#e8e0dc] rounded-lg px-2 text-[13px] text-center outline-none focus:border-[#887267]" />
              <span className="text-gray-400 font-semibold">-</span>
              <input ref={ref3} value={p3} onChange={e => handlePhoneChange(2, e.target.value, 4, null)} maxLength={4} placeholder="0000"
                className="w-20 h-[34px] border border-[#e8e0dc] rounded-lg px-2 text-[13px] text-center outline-none focus:border-[#887267]" />
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col flex-1">
        <p className="text-[13px] font-semibold text-[#2a1f1a] mb-2">호출 안내 메세지</p>
        <textarea value={message} onChange={e => setMessage(e.target.value)}
          className="w-full lg:w-[467px] h-[65px] border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#887267] resize-none" />
      </div>
    </div>
  );
}

// =============================================
// 고대비
// =============================================
function HighContrastContent({ data, solutionCode, onSaved, saveRef, setSaving, showModal }: ContentProps) {
  const toState = (arr?: string[]) => ({
    "yellow-black": arr?.includes("yellow-black") ?? true,
    "white-black": arr?.includes("white-black") ?? false,
  });

  const [selected, setSelected] = useState(toState(data?.high_contrast));
  useEffect(() => { setSelected(toState(data?.high_contrast)); }, [data]);

  const toggle = (key: "yellow-black" | "white-black") => {
    const otherKey = key === "yellow-black" ? "white-black" : "yellow-black";
    if ((selected as any)[key] && !(selected as any)[otherKey]) {
      showModal("고대비 두 가지 중 하나는 필수로 선택해야 합니다.");
      return;
    }
    setSelected(prev => ({ ...prev, [key]: !(prev as any)[key] }));
  };

  saveRef.current = async () => {
    setSaving(true);
    try {
      const arr = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
      await patchSetting(solutionCode, "accessibility", { high_contrast: arr });
      showModal("고대비 설정이 저장되었습니다.");
      onSaved?.();
    } catch (e: any) {
      showModal(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-[13px] font-semibold text-[#2a1f1a] mb-3">고대비 테마 선택</p>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => toggle("yellow-black")}
            className="w-full sm:w-[192px] h-[36px] bg-black border rounded-lg flex items-center justify-center gap-2"
            style={{ borderColor: selected["yellow-black"] ? "#FFD52B" : "#B2B2B2" }}>
            <span className="text-[13px] font-semibold" style={{ color: "#FFD52B" }}>노란색 - 검정색 대비 모드</span>
          </button>
          <div className="w-[24px] h-[22px] border rounded-lg flex items-center justify-center bg-white cursor-pointer flex-shrink-0"
            style={{ borderColor: "#887267" }} onClick={() => toggle("yellow-black")}>
            {selected["yellow-black"] && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#887267" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => toggle("white-black")}
            className="w-full sm:w-[192px] h-[36px] bg-black border rounded-lg flex items-center justify-center gap-2"
            style={{ borderColor: selected["white-black"] ? "#887267" : "#B2B2B2" }}>
            <span className="text-[13px] font-semibold text-white">흰색 - 검정색 대비 모드</span>
          </button>
          <div className="w-[24px] h-[22px] border rounded-lg flex items-center justify-center bg-white cursor-pointer flex-shrink-0"
            style={{ borderColor: "#887267" }} onClick={() => toggle("white-black")}>
            {selected["white-black"] && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#887267" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================
// 음성
// =============================================
function VolumeContent({ data, solutionCode, onSaved, saveRef, setSaving, showModal }: ContentProps) {
  const options = [
    { label: "낮음", value: 25 },
    { label: "보통", value: 50 },
  ];
  const nearest = (val?: number) => {
    if (val == null) return 50;
    return options.reduce((prev, curr) => Math.abs(curr.value - val) < Math.abs(prev.value - val) ? curr : prev).value;
  };

  const [selected, setSelected] = useState(nearest(data?.default_volume));
  useEffect(() => { setSelected(nearest(data?.default_volume)); }, [data]);

  saveRef.current = async () => {
    setSaving(true);
    try {
      await patchSetting(solutionCode, "audio", { default_volume: selected });
      showModal("음성 설정이 저장되었습니다.");
      onSaved?.();
    } catch (e: any) {
      showModal(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 mb-3">
        <p className="text-[13px] font-semibold text-[#2a1f1a]">음성 기본값 설정</p>
        <p className="text-[11px] text-gray-400">키오스크의 음성 볼륨 기본값을 설정합니다.</p>
      </div>
      <div className="flex flex-wrap gap-3">
        {options.map(opt => (
          <button key={opt.value} onClick={() => setSelected(opt.value)}
            className={`h-[32px] px-4 border rounded-lg text-[13px] font-semibold transition-colors ${selected === opt.value ? "bg-[#887267] border-[#887267] text-white" : "bg-white border-[#e8e0dc] text-[#2a1f1a]"}`}>
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// =============================================
// 언어
// =============================================
const LANG_OPTIONS = [
  { key: "ko", display: "한국어" },
  { key: "en", display: "English" },
  { key: "zh", display: "中文 (简体)" },
  { key: "ja", display: "日本語" },
  { key: "ru", display: "Русский" },
  { key: "uz", display: "O'zbek" },
  { key: "mn", display: "Монгол" },
  { key: "th", display: "ภาษาไทย" },
];

function LanguageContent({ data, solutionCode, onSaved, saveRef, setSaving, showModal }: ContentProps) {
  const [baseLanguage, setBaseLanguage] = useState(data?.default_language || "ko");
  const [displayLangs, setDisplayLangs] = useState<Record<string, boolean>>(
    Object.fromEntries(LANG_OPTIONS.map(l => [l.key, data?.supported_languages?.includes(l.key) ?? (l.key === "ko" || l.key === "en")]))
  );

  useEffect(() => {
    setBaseLanguage(data?.default_language || "ko");
    setDisplayLangs(Object.fromEntries(LANG_OPTIONS.map(l => [l.key, data?.supported_languages?.includes(l.key) ?? (l.key === "ko" || l.key === "en")])));
  }, [data]);

  const toggleLang = (key: string) => {
    const enabledCount = Object.values(displayLangs).filter(Boolean).length;
    if (displayLangs[key] && enabledCount === 1) {
      showModal("표시 언어는 최소 하나 이상 선택해야 합니다.");
      return;
    }
    setDisplayLangs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  saveRef.current = async () => {
    setSaving(true);
    try {
      const supported = LANG_OPTIONS.filter(l => displayLangs[l.key]).map(l => l.key);
      await patchSetting(solutionCode, "language", { default_language: baseLanguage, supported_languages: supported });
      showModal("언어 설정이 저장되었습니다.");
      onSaved?.();
    } catch (e: any) {
      showModal(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-12">
      <div>
        <p className="text-[13px] font-semibold text-[#2a1f1a] mb-2">기본 언어 설정</p>
        <div className="relative">
          <select value={baseLanguage} onChange={e => setBaseLanguage(e.target.value)}
            className="appearance-none w-full sm:w-[160px] h-[35px] px-3 pr-8 border border-[#e8e0dc] rounded-lg bg-white text-[12px] font-semibold text-[#2a1f1a] outline-none focus:border-[#887267]">
            {LANG_OPTIONS.map(l => <option key={l.key} value={l.key}>{l.display}</option>)}
          </select>
          <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#887267" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
          </div>
        </div>
      </div>
      <div>
        <p className="text-[13px] font-semibold text-[#2a1f1a] mb-2">표시 언어 종류 선택</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-2">
          {LANG_OPTIONS.map(lang => (
            <div key={lang.key} className="flex items-center gap-2">
              <div className="flex-1 h-[32px] bg-[#2a1f1a] rounded-lg flex items-center justify-center">
                <span className="text-white text-[11px] font-semibold">{lang.display}</span>
              </div>
              <div onClick={() => toggleLang(lang.key)}
                className="w-[22px] h-[20px] border border-[#887267] rounded-lg bg-white flex items-center justify-center cursor-pointer flex-shrink-0">
                {displayLangs[lang.key] && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#887267" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =============================================
// 화면확대
// =============================================
function ZoomContent({ data, solutionCode, onSaved, saveRef, setSaving, showModal }: ContentProps) {
  const options = [150, 200];
  const [selected, setSelected] = useState(data?.default_zoom_level ?? 150);
  useEffect(() => { setSelected(data?.default_zoom_level ?? 150); }, [data]);

  saveRef.current = async () => {
    setSaving(true);
    try {
      await patchSetting(solutionCode, "display", { default_zoom_level: selected });
      showModal("화면확대 설정이 저장되었습니다.");
      onSaved?.();
    } catch (e: any) {
      showModal(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-[13px] font-semibold text-[#2a1f1a] mb-3">확대 비율 기본 설정</p>
      <div className="flex gap-3">
        {options.map(opt => (
          <button key={opt} onClick={() => setSelected(opt)}
            className={`h-[32px] px-4 border rounded-lg text-[13px] font-semibold transition-colors ${selected === opt ? "bg-[#887267] border-[#887267] text-white" : "bg-white border-[#e8e0dc] text-[#2a1f1a]"}`}>
            {opt}%
          </button>
        ))}
      </div>
    </div>
  );
}

// =============================================
// 고객센터
// =============================================
function CustomerServiceContent({ data, solutionCode, onSaved, saveRef, setSaving, showModal }: ContentProps) {
  const [message, setMessage] = useState(data?.customer_service_message || "");
  const [phone, setPhone] = useState(data?.customer_support_phone || "");

  useEffect(() => {
    setMessage(data?.customer_service_message || "");
    setPhone(data?.customer_support_phone || "");
  }, [data]);

  saveRef.current = async () => {
    setSaving(true);
    try {
      await patchSetting(solutionCode, "customer-support", { customer_service_message: message, customer_support_phone: phone });
      showModal("고객센터 설정이 저장되었습니다.");
      onSaved?.();
    } catch (e: any) {
      showModal(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
      <div className="flex-1">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 mb-2">
          <p className="text-[13px] font-semibold text-[#2a1f1a]">고객센터 메세지 문구</p>
          <p className="text-[11px] text-gray-400">고객센터 콜 번호 앞에 문구가 표기됩니다.</p>
        </div>
        <input value={message} onChange={e => setMessage(e.target.value)} placeholder="메세지 입력 ..."
          className="w-full h-[35px] border border-[#e8e0dc] rounded-lg px-3 text-[12px] outline-none focus:border-[#887267]" />
      </div>
      <div className="hidden lg:flex items-end pb-2 text-gray-300 text-xl font-semibold">:</div>
      <div className="flex-1">
        <p className="text-[13px] font-semibold text-[#2a1f1a] mb-2">고객센터 전화번호</p>
        <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="전화번호 입력 ..."
          className="w-full h-[35px] border border-[#e8e0dc] rounded-lg px-3 text-[12px] outline-none focus:border-[#887267]" />
      </div>
    </div>
  );
}

// =============================================
// 화면전환
// =============================================
function ScreenTransitionContent({ data, solutionCode, onSaved, saveRef, setSaving, showModal }: ContentProps) {
  const homeOptions = [5, 10, 15, 20, 25];
  const [homeTime, setHomeTime] = useState(data?.home_return_minutes ?? 10);
  useEffect(() => { setHomeTime(data?.home_return_minutes ?? 10); }, [data]);

  saveRef.current = async () => {
    setSaving(true);
    try {
      await patchSetting(solutionCode, "display", { home_return_minutes: homeTime });
      showModal("화면전환 설정이 저장되었습니다.");
      onSaved?.();
    } catch (e: any) {
      showModal(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 mb-3">
        <p className="text-[13px] font-semibold text-[#2a1f1a]">홈 화면 복구 시간</p>
        <p className="text-[11px] text-gray-400">화면을 몇 분동안 조작하지 않으면 홈 화면으로 돌아가는지 설정</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {homeOptions.map(opt => (
          <button key={opt} onClick={() => setHomeTime(opt)}
            className={`h-[32px] px-3 border rounded-lg text-[13px] font-semibold transition-colors ${homeTime === opt ? "bg-[#887267] border-[#887267] text-white" : "bg-white border-[#e8e0dc] text-[#2a1f1a]"}`}>
            {opt} m
          </button>
        ))}
      </div>
    </div>
  );
}

// =============================================
// 아코디언
// =============================================
function AccordionItem({ itemKey, label, icon, isOpen, onToggle, ContentComponent, data, solutionCode, onSaved, showModal }: {
  itemKey: string; label: string; icon: React.ReactNode; isOpen: boolean;
  onToggle: (key: string) => void; ContentComponent: React.ComponentType<ContentProps>;
  data: any; solutionCode: string; onSaved?: () => void; showModal: (title: string, type?: string) => void;
}) {
  const saveRef = useRef<(() => Promise<void>) | null>(null);
  const [saving, setSaving] = useState(false);

  return (
    <div className="border border-[#e8e0dc] rounded-2xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
      <div className="w-full flex items-center justify-between px-5 h-[52px] cursor-pointer" onClick={() => onToggle(itemKey)}>
        <div className="flex items-center gap-2">
          <div className={isOpen ? "text-[#887267]" : "text-gray-400"}>{icon}</div>
          <span className={`text-[14px] font-semibold ${isOpen ? "text-[#2a1f1a]" : "text-gray-400"}`}>{label}</span>
        </div>
        <div className="flex items-center gap-2">
          {isOpen && (
            <button onClick={e => { e.stopPropagation(); saveRef.current?.(); }} disabled={saving}
              className="px-3 h-[28px] bg-white border border-[#887267] rounded-lg text-[#887267] text-[12px] font-medium disabled:opacity-50 hover:bg-[#f5f2f0] transition-colors">
              {saving ? "..." : "저장"}
            </button>
          )}
          {isOpen ? <ChevronUp /> : <ChevronDown />}
        </div>
      </div>
      {isOpen && (
        <div className="border-t border-[#f5f2f0] bg-white px-5 py-6">
          <ContentComponent data={data} solutionCode={solutionCode} onSaved={onSaved} saveRef={saveRef} setSaving={setSaving} showModal={showModal} />
        </div>
      )}
    </div>
  );
}

// =============================================
// 메인
// =============================================
export default function AdminAccessibilitySettings() {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [setting, setSetting] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState({ isOpen: false, title: "" });

  const kioskCode = localStorage.getItem("kiosk_code") || "";
  const solutionCode = setting?.solution_code;

  const showModal = (title: string) => setModal({ isOpen: true, title });
  const closeModal = () => setModal(prev => ({ ...prev, isOpen: false }));

  const loadSetting = async () => {
    if (!kioskCode) { setError("키오스크 정보가 없습니다."); setLoading(false); return; }
    setError(null);
    try {
      setLoading(true);
      const data = await fetchSettingsByKiosk(kioskCode);
      setSetting(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSetting(); }, [kioskCode]);

  const toggleItem = (key: string) => setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));

  const settingItems = [
    { key: "직원호출", label: "직원호출 설정", icon: <EmployeeCallIcon />, component: EmployeeCallContent, data: setting?.staff_call },
    { key: "고대비", label: "고대비 설정", icon: <HighContrastIcon />, component: HighContrastContent, data: setting?.accessibility },
    { key: "음성", label: "음성 설정", icon: <VolumeIcon />, component: VolumeContent, data: setting?.audio },
    { key: "언어", label: "언어 설정", icon: <LanguageIcon />, component: LanguageContent, data: setting?.language },
    { key: "화면확대", label: "화면확대 설정", icon: <ZoomIcon />, component: ZoomContent, data: setting?.display },
    { key: "고객센터", label: "고객센터 설정", icon: <CustomerServiceIcon />, component: CustomerServiceContent, data: setting?.customer_support },
    { key: "화면전환", label: "화면전환 시간 설정", icon: <ScreenTransitionIcon />, component: ScreenTransitionContent, data: setting?.display },
  ];

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-6">
      <div className="max-w-[1000px] mx-auto">

        <div className="flex items-center gap-3 mb-6">
          <SettingsIcon />
          <h1 className="text-[22px] font-extrabold text-[#2a1f1a]">접근성 관리</h1>
        </div>

        {loading ? (
          <div className="py-20 text-center text-gray-400 text-[14px]">불러오는 중...</div>
        ) : error ? (
          <div className="py-20 text-center text-gray-400 text-[14px]">{error}</div>
        ) : (
          <div className="flex flex-col gap-3">
            {settingItems.map(item => (
              <AccordionItem
                key={item.key}
                itemKey={item.key}
                label={item.label}
                icon={item.icon}
                isOpen={!!openItems[item.key]}
                onToggle={toggleItem}
                ContentComponent={item.component}
                data={item.data}
                solutionCode={solutionCode}
                onSaved={loadSetting}
                showModal={showModal}
              />
            ))}
          </div>
        )}
      </div>

      {/* 결과 모달 */}
      {modal.isOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[320px] mx-4 p-6 text-center">
            <p className="text-[14px] font-semibold text-[#2a1f1a] mb-4">{modal.title}</p>
            <button onClick={closeModal} className="w-full py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] text-[13px] font-semibold">확인</button>
          </div>
        </div>
      )}
    </div>
  );
}